import { useTranslation } from '../i18n'
import type { ProjectDashboardItem } from '../types'

interface FeatureCompletenessCardProps {
  project: ProjectDashboardItem
}

export function FeatureCompletenessCard({ project }: FeatureCompletenessCardProps) {
  const { t } = useTranslation()
  const { featureCompleteness } = project
  const { passedCount, failedCount, errorCount, totalCount, skippedCount, completionPercentage } =
    featureCompleteness

  const isFeatureComplete = completionPercentage === 100 && passedCount === totalCount
  const failedTotal = failedCount + errorCount

  const passedWidth = totalCount > 0 ? (passedCount / totalCount) * 100 : 0
  const failedWidth = totalCount > 0 ? (failedTotal / totalCount) * 100 : 0

  return (
    <div className="card" data-testid={`feature-completeness-${project.id}`}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h4 style={{ margin: 0 }}>{project.name}</h4>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <span
            style={{
              fontSize: '0.875rem',
              fontWeight: 500,
              color: isFeatureComplete ? 'var(--color-success)' : 'var(--color-text)',
            }}
          >
            {completionPercentage}%
          </span>
          {isFeatureComplete && (
            <span
              data-testid="feature-complete-badge"
              style={{
                backgroundColor: 'var(--color-success)',
                color: 'white',
                padding: '0.25rem 0.5rem',
                borderRadius: '4px',
                fontSize: '0.75rem',
                fontWeight: 600,
              }}
            >
              {t('status.featureComplete')}
            </span>
          )}
        </div>
      </div>

      <div style={{ marginTop: '1rem' }}>
        <div style={{ marginBottom: '0.5rem' }}>
          <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
            {passedCount} {t('label.passed').toLowerCase()}, {failedTotal}{' '}
            {t('label.failed').toLowerCase()}
            {skippedCount > 0 && `, ${skippedCount} ${t('label.skipped').toLowerCase()}`}
          </span>
        </div>

        <div
          style={{
            height: '8px',
            backgroundColor: 'var(--color-border)',
            borderRadius: '4px',
            overflow: 'hidden',
            display: 'flex',
          }}
        >
          <div
            data-testid="progress-bar-passed"
            style={{
              height: '100%',
              width: `${passedWidth}%`,
              backgroundColor: 'var(--color-success)',
              transition: 'width 0.3s ease',
            }}
          />
          <div
            data-testid="progress-bar-failed"
            style={{
              height: '100%',
              width: `${failedWidth}%`,
              backgroundColor: 'var(--color-error)',
              transition: 'width 0.3s ease',
            }}
          />
        </div>
      </div>
    </div>
  )
}
